import { Loader2, MapPin, Star } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { OrderNotificationsToggle } from "@/features/delivery/components/order-notifications-toggle";
import {
  type SupportCategory,
  useDeliveryForOrder,
  useDeliveryTracking,
  useOrderRating,
  useSubmitRating,
  useSubmitSupport,
} from "@/features/delivery/hooks/use-delivery-tracking";
import { useOrder } from "@/hooks/use-orders";
import { useOrderEvents } from "@/hooks/use-order-events";
import { authClient } from "@/lib/auth-client";

const STATUS_LABELS: Record<string, string> = {
  pending: "tracking.statusPending",
  accepted: "tracking.statusAccepted",
  in_progress: "tracking.statusInProgress",
  ready: "tracking.statusReady",
  delivered: "tracking.statusDelivered",
};

const DELIVERY_STATE_LABELS: Record<string, string> = {
  assigned: "tracking.statusAccepted",
  accepted: "tracking.statusAccepted",
  en_route: "tracking.statusEnRoute",
  arrived: "tracking.statusArrived",
  delivered: "tracking.statusDelivered",
};

export default function DeliveryOrderStatusPage() {
  const { t } = useTranslation();
  const { id, orgSlug } = useParams<{ id: string; orgSlug: string }>();
  const { data: session } = authClient.useSession();
  const orderQuery = useOrder(id);
  useOrderEvents(id ? { type: "order", orderId: id } : { type: "org" });

  const deliveryQuery = useDeliveryForOrder(id);
  const deliveryId = deliveryQuery.data?.id;
  const tracking = useDeliveryTracking(deliveryId ?? undefined);

  const authenticatedOrderId = session ? id : undefined;
  const ratingQuery = useOrderRating(authenticatedOrderId);
  const submitRating = useSubmitRating(authenticatedOrderId);
  const submitSupport = useSubmitSupport(authenticatedOrderId);

  const [ratingOpen, setRatingOpen] = useState(false);
  const [stars, setStars] = useState(5);
  const [comment, setComment] = useState("");
  const [supportOpen, setSupportOpen] = useState(false);
  const [supportCategory, setSupportCategory] = useState<SupportCategory>("other");
  const [supportMessage, setSupportMessage] = useState("");

  const order = orderQuery.data?.order;

  useEffect(() => {
    if (session && order?.status === "delivered" && ratingQuery.data === null && !ratingOpen) {
      setRatingOpen(true);
    }
  }, [order?.status, ratingQuery.data, ratingOpen, session]);

  if (orderQuery.isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (!order) {
    return null;
  }

  const addressSnapshot = deliveryQuery.data?.addressSnapshot ?? null;

  const handleSubmitRating = async () => {
    try {
      await submitRating.mutateAsync({ stars, comment: comment || null });
      toast.success(t("rating.thanks", "¡Gracias por tu calificación!"));
      setRatingOpen(false);
    } catch (error) {
      toast.error((error as Error).message);
    }
  };

  const handleSubmitSupport = async () => {
    if (!supportMessage.trim()) {
      return;
    }
    try {
      await submitSupport.mutateAsync({
        category: supportCategory,
        message: supportMessage.trim(),
      });
      toast.success(t("support.submitted", "Recibimos tu mensaje."));
      setSupportOpen(false);
      setSupportMessage("");
    } catch (error) {
      toast.error((error as Error).message);
    }
  };

  const deliveryState = tracking.data?.state ?? null;

  return (
    <div className="mx-auto max-w-2xl space-y-3 p-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">
          {t("tracking.title", "Seguimiento del pedido")} #{order.dailyNumber}
        </h1>
        <Badge variant="secondary">
          {t(STATUS_LABELS[order.status] ?? order.status, order.status)}
        </Badge>
      </div>

      {order.fulfillmentType === "delivery" && deliveryQuery.data ? (
        <Link to={`/m/${orgSlug}/delivery/order/${id}/track`}>
          <Button type="button" className="w-full">
            <MapPin className="h-4 w-4" />
            {t("tracking.followOnMap", "Seguir en el mapa")}
          </Button>
        </Link>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("tracking.statusTimeline", "Estado")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-1 text-sm">
          {deliveryState ? (
            <p>{t(DELIVERY_STATE_LABELS[deliveryState] ?? deliveryState, deliveryState)}</p>
          ) : (
            <p>{t(STATUS_LABELS[order.status] ?? order.status, order.status)}</p>
          )}
          {addressSnapshot ? (
            <p className="flex items-center gap-1 text-muted-foreground">
              <MapPin className="h-4 w-4" />
              {addressSnapshot.label ?? ""} — {addressSnapshot.line1 ?? ""}
            </p>
          ) : null}
        </CardContent>
      </Card>

      <div className="flex flex-wrap justify-end gap-2">
        {id ? <OrderNotificationsToggle orderId={id} /> : null}
        {session ? (
          <Button type="button" variant="outline" size="sm" onClick={() => setSupportOpen(true)}>
            {t("support.open", "Necesito ayuda")}
          </Button>
        ) : null}
      </div>

      <Dialog open={ratingOpen} onOpenChange={setRatingOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("rating.title", "Calificá tu pedido")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((value) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setStars(value)}
                  className={value <= stars ? "text-yellow-500" : "text-muted-foreground"}
                >
                  <Star className="h-7 w-7 fill-current" />
                </button>
              ))}
            </div>
            <Textarea
              placeholder={t("rating.comment", "Comentario (opcional)")}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
              maxLength={1000}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRatingOpen(false)}>
              {t("common.cancel", "Cancelar")}
            </Button>
            <Button onClick={handleSubmitRating} disabled={submitRating.isPending}>
              {t("rating.submit", "Enviar calificación")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={supportOpen} onOpenChange={setSupportOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("support.title", "Solicitar ayuda")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <label className="text-sm font-medium">
                {t("support.category", "Tipo de problema")}
              </label>
              <Select
                value={supportCategory}
                onValueChange={(value) => setSupportCategory(value as SupportCategory)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="rider_issue">
                    {t("support.categoryRiderIssue", "Problema con el repartidor")}
                  </SelectItem>
                  <SelectItem value="food_issue">
                    {t("support.categoryFoodIssue", "Problema con la comida")}
                  </SelectItem>
                  <SelectItem value="missing_item">
                    {t("support.categoryMissingItem", "Falta un producto")}
                  </SelectItem>
                  <SelectItem value="wrong_item">
                    {t("support.categoryWrongItem", "Producto incorrecto")}
                  </SelectItem>
                  <SelectItem value="late">
                    {t("support.categoryLate", "Pedido demorado")}
                  </SelectItem>
                  <SelectItem value="other">{t("support.categoryOther", "Otro")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Textarea
              placeholder={t("support.message", "Contanos qué pasó")}
              value={supportMessage}
              onChange={(e) => setSupportMessage(e.target.value)}
              rows={4}
              maxLength={2000}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSupportOpen(false)}>
              {t("common.cancel", "Cancelar")}
            </Button>
            <Button
              onClick={handleSubmitSupport}
              disabled={submitSupport.isPending || !supportMessage.trim()}
            >
              {t("support.submit", "Enviar")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
